import React from 'react';

import {
  formatActivityNameList,
  getActivityClauseSeparator,
  getTaskAssignmentClauses,
  withUnresolvedNames,
} from '@aline/common';
import type { ActivityClause, AssignmentSide, TaskAssignmentSides } from '@aline/common';
import { Typography } from '@mui/material';
import { MessageSquareText } from 'lucide-react';
import { AlineAvatar, AlineTooltip } from 'src/components';
import { AlineAIIcon } from 'src/components/icons/AlineAIIcon';
import { Apollo } from 'src/gql/generated';
import { getAssigneeDisplayDetails } from 'src/helpers';
import { makeStyles } from 'tss-react/mui';

import { FALLBACK_STATUS_META, STATUS_EVENT_META } from './activity-status-meta';
import type { ActivityItemFragment, StatusUpdateActivityBody } from './activity-types';

type StatusEvent = StatusUpdateActivityBody['events'][number];

/** Review requests name the reviewers asked; older re-review rows keep their wording. */
const REVIEW_REQUEST_LEAD: Partial<Record<Apollo.ActivityStatusEventType, string>> = {
  [Apollo.ActivityStatusEventType.TaskReReviewRequested]: 'requested re-review from',
  [Apollo.ActivityStatusEventType.TaskSubmittedForReview]: 'requested review from',
};

const useStyles = makeStyles()(theme => ({
  actor: {
    fontWeight: 600,
  },
  iconSlot: {
    alignItems: 'center',
    display: 'flex',
    flexShrink: 0,
    height: 24,
    justifyContent: 'center',
    width: 24,
  },
  noteIcon: {
    color: theme.appColors.iconsType,
    cursor: 'default',
    display: 'inline-flex',
    marginLeft: theme.spacing(0.75),
    verticalAlign: 'middle',
  },
  noteTooltip: {
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  },
  root: {
    alignItems: 'flex-start',
    display: 'flex',
    gap: theme.spacing(1),
    padding: theme.spacing(0.5, 1),
  },
  sentence: {
    color: theme.appColors.iconsType,
    fontSize: 12,
    lineHeight: 1.4,
  },
  // As tall as the icon slot, so a one-line row stays centered on it.
  text: {
    alignItems: 'center',
    display: 'flex',
    minHeight: 24,
    minWidth: 0,
  },
  time: {
    color: theme.appColors.iconsType,
    fontSize: 12,
    marginLeft: theme.spacing(1),
    opacity: 0.5,
    whiteSpace: 'nowrap',
  },
}));

interface ActivityStatusRowProps {
  author: ActivityItemFragment['author'];
  body: StatusUpdateActivityBody;
  time: string;
  /** AI-sourced row: display the "Aline AI" entity instead of the acting user. */
  isAi?: boolean;
}

export function ActivityStatusRow({
  author,
  body,
  isAi = false,
  time,
}: ActivityStatusRowProps): JSX.Element | null {
  const { classes } = useStyles();
  const { events } = body;
  // A malformed payload can yield no events; render nothing rather than crash on events[0].
  if (!events.length) return null;
  const note = getStatusEventsNote(events);
  const actorName = isAi
    ? 'Aline AI'
    : author
      ? getAssigneeDisplayDetails(author).primary
      : 'Someone';

  return (
    <div className={classes.root}>
      <span className={classes.iconSlot}>
        <StatusRowIcon author={author} events={events} isAi={isAi} />
      </span>
      <div className={classes.text}>
        <Typography className={classes.sentence} component="span" variant="body2">
          <span className={classes.actor}>{actorName}</span>
          {events.map((event, index) => (
            <EventPhrase
              key={index}
              authorId={author?.id ?? null}
              connector={connectorFor(index, events.length)}
              event={event}
              isAi={isAi}
            />
          ))}
          <Typography className={classes.time} component="span" variant="caption">
            {time}
          </Typography>
          {Boolean(note) && (
            <AlineTooltip
              arrow
              placement="top"
              title={<span className={classes.noteTooltip}>{note}</span>}
            >
              <span aria-label={`Note: ${note}`} className={classes.noteIcon} tabIndex={0}>
                <MessageSquareText size={14} />
              </span>
            </AlineTooltip>
          )}
        </Typography>
      </div>
    </div>
  );
}

/**
 * The note someone left with a review request, reminder or request for changes, newest first. The
 * row shows it behind an icon, since the people it was meant for already got it by email.
 */
export function getStatusEventsNote(
  events: readonly Pick<StatusEvent, 'data' | 'eventType'>[]
): string | null {
  const notes: unknown[] = events.map(event => event.data?.note);
  return notes.findLast(getIsNonBlankString) ?? null;
}

/** Aline AI's mark, the actor's avatar for a batch of events, or the one event's own icon. */
function StatusRowIcon({
  author,
  events,
  isAi,
}: {
  author: ActivityItemFragment['author'];
  events: StatusUpdateActivityBody['events'];
  isAi: boolean;
}): JSX.Element | null {
  if (isAi) return <AlineAIIcon height={20} width={20} />;
  if (events.length > 1) return <AlineAvatar avatarInfo={author} />;
  const onlyEvent = events.at(0);
  if (!onlyEvent) return null;
  const meta = STATUS_EVENT_META[onlyEvent.eventType] ?? FALLBACK_STATUS_META;
  return meta.renderIcon(20, meta.color);
}

function connectorFor(index: number, total: number): string {
  if (index === 0) return ' ';
  if (index === total - 1) return total > 2 ? ', and ' : ' and ';
  return ', ';
}

interface EventPhraseProps {
  authorId: string | null;
  connector: string;
  event: StatusEvent;
  isAi: boolean;
}

function EventPhrase({ authorId, connector, event, isAi }: EventPhraseProps): JSX.Element {
  const clauses = describeStatusEvent(event, { authorId, isAi });
  return (
    <>
      {connector}
      {clauses.map((clause, index) => (
        <React.Fragment key={index}>
          {getActivityClauseSeparator(index, clauses.length)}
          {clause.lead}
          {clause.targetName && <span style={{ fontWeight: 600 }}> {clause.targetName}</span>}
          {clause.trail && ` ${clause.trail}`}
        </React.Fragment>
      ))}
    </>
  );
}

export function describeStatusEvent(
  event: StatusEvent,
  options: { authorId: string | null; isAi?: boolean }
): ActivityClause[] {
  const { authorId, isAi = false } = options;
  if (event.eventType === Apollo.ActivityStatusEventType.TaskAssigned) {
    const { assignmentChange } = event;
    if (assignmentChange === Apollo.ActivityAssignmentChange.AiDelegated)
      return [{ lead: 'delegated the task to', targetName: 'Aline AI' }];
    if (assignmentChange === Apollo.ActivityAssignmentChange.AiDelegationRemoved)
      return [{ lead: 'removed Aline AI delegation' }];

    /**
     * A batch that also dropped people says so. Without that clause a replacement reads as an
     * addition, and anyone scanning the feed would think the previous people are still on the task.
     */
    const clauses = getTaskAssignmentClauses(getAssignmentSides(event, { authorId, isAi }));
    if (clauses.length > 0) return clauses;
    // A coalesced run that cancelled itself out changed nobody; name the act, not a result.
    if (assignmentChange === Apollo.ActivityAssignmentChange.Updated)
      return [{ lead: 'updated the task assignees' }];
    return [{ lead: 'assigned the task' }];
  }

  if (event.eventType === Apollo.ActivityStatusEventType.WorkflowPriorityChanged) {
    const nextPriority = event.data?.nextPriority;
    if (typeof nextPriority === 'string')
      return [{ lead: 'changed the priority to', targetName: nextPriority }];
    return [{ lead: 'changed the priority' }];
  }

  const meta = STATUS_EVENT_META[event.eventType] ?? FALLBACK_STATUS_META;
  const reviewRequestLead = REVIEW_REQUEST_LEAD[event.eventType];
  if (reviewRequestLead && event.reviewRequestUsers.length > 0) {
    const names = event.reviewRequestUsers.map(user => getAssigneeDisplayDetails(user).primary);
    return [{ lead: reviewRequestLead, targetName: formatActivityNameList(names) }];
  }
  if (event.onBehalfOfUser) {
    const lead =
      event.eventType === Apollo.ActivityStatusEventType.TaskAssigneeCompleted
        ? 'marked the task complete for'
        : `${meta.verb} for`;
    return [{ lead, targetName: getAssigneeDisplayDetails(event.onBehalfOfUser).primary }];
  }
  return [{ lead: meta.verb }];
}

/**
 * Splits the people an assignment event named by the side of the task they joined or left. Counts
 * come from the payload, so a since-deleted account is stood in for rather than dropped.
 */
function getAssignmentSides(
  event: StatusEvent,
  options: { authorId: string | null; isAi: boolean }
): TaskAssignmentSides {
  const { authorId, isAi } = options;
  const reviewerIds = new Set(event.reviewerIds);

  function toSide(
    sides: { roles: StatusEvent['targetRoles']; users: StatusEvent['targetUsers'] },
    count: number,
    isReviewer: boolean
  ): AssignmentSide {
    const users = sides.users.filter(user => reviewerIds.has(user.id) === isReviewer);
    const roles = sides.roles.filter(role => reviewerIds.has(role.id) === isReviewer);
    const [onlyUser] = users;
    return {
      count,
      /**
       * Needs the payload's count, not the resolved list: with an unresolvable second person the
       * list would be one user long and a two-person batch would read as the actor acting on
       * themselves.
       */
      isActor:
        !isAi &&
        count === 1 &&
        users.length === 1 &&
        roles.length === 0 &&
        onlyUser?.id === authorId,
      names: withUnresolvedNames(namesOf(users, roles), count),
    };
  }

  const added = { roles: event.targetRoles, users: event.targetUsers };
  const moved = { roles: event.movedRoles, users: event.movedUsers };
  const removed = { roles: event.removedRoles, users: event.removedUsers };
  return {
    addedAssignees: toSide(added, event.targetCount - event.targetReviewerCount, false),
    addedReviewers: toSide(added, event.targetReviewerCount, true),
    madeAssignees: toSide(moved, event.movedCount - event.movedReviewerCount, false),
    madeReviewers: toSide(moved, event.movedReviewerCount, true),
    removedAssignees: toSide(removed, event.removedCount - event.removedReviewerCount, false),
    removedReviewers: toSide(removed, event.removedReviewerCount, true),
  };
}

function getIsNonBlankString(value: unknown): value is string {
  return typeof value === 'string' && Boolean(value.trim());
}

/** Display names for one side of an assignment change, users before roles. */
function namesOf(users: StatusEvent['targetUsers'], roles: StatusEvent['targetRoles']): string[] {
  return [
    ...users.map(user => getAssigneeDisplayDetails(user).primary),
    ...roles.map(role => role.roleName),
  ];
}
